import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../Context/ThemeContext';
import { useDashboardData, useGameHistory, useTopPerformers } from './hooks';
import { calculatePercentageChange } from './utils/mappers';
import { defaultPeriodState, type PeriodOption, type ChartDataPoint, type PeriodState } from './types';
import {
  UniversalFilter,
  StatCard,
  DailyChallengeCard,
  ChartCard,
  ChartKey,
  RevenueCard,
  PerformersCard,
  BottomStatCard,
} from './Components';

// Helpers
const getPeriodLabel = (period: PeriodOption): string => {
  switch (period) {
    case '1D': return 'from yesterday';
    case '7D': return 'from last week';
    case '30D': return 'from last month';
    case '90D': return 'from last quarter';
  }
};

const Dashboard: React.FC = () => {
  const { theme, isMobile, isTablet } = useTheme();
  const navigate = useNavigate();

  // Consolidated period state
  const [periods, setPeriods] = useState<PeriodState>(defaultPeriodState);

  // Chart line visibility
  const [activeLines, setActiveLines] = useState<Record<ChartKey, boolean>>({
    daily: true,
    vsRandom: true,
    vsFriends: true,
    vsBot: true,
    single: true,
  });

  // Custom hooks for data fetching
  const { statsData, loading: statsLoading, error: statsError, retry } = useDashboardData();
  const { data: chartData, totalGames: totalGamesPlayed, loading: chartLoading } = useGameHistory(periods.chart);
  const { performers: topPerformers, loading: topPerformersLoading } = useTopPerformers(periods.topPerformers);

  // Historical data for stat calculations (cached by hooks)
  // Each hook now returns comparison data for accurate period-over-period comparisons
  const {
    data: dailyData,
    vsGamesComparison: vsGames7DComparison,
    singleGamesComparison: singleGames7DComparison,
    vsGamesCompletedComparison: vsGamesCompleted7DComparison,
    dailyChallengeComparison: dailyChallenge7DComparison,
    vsBotComparison: vsBot7DComparison,
  } = useGameHistory('7D');
  const {
    data: weeklyData,
    vsGamesComparison: vsGames30DComparison,
    singleGamesComparison: singleGames30DComparison,
    vsGamesCompletedComparison: vsGamesCompleted30DComparison,
    dailyChallengeComparison: dailyChallenge30DComparison,
    vsBotComparison: vsBot30DComparison,
  } = useGameHistory('30D');
  const {
    data: monthlyData,
    vsGamesComparison: vsGames90DComparison,
    singleGamesComparison: singleGames90DComparison,
    vsGamesCompletedComparison: vsGamesCompleted90DComparison,
    dailyChallengeComparison: dailyChallenge90DComparison,
    vsBotComparison: vsBot90DComparison,
  } = useGameHistory('90D');

  // Period update helpers
  const updatePeriod = <K extends keyof PeriodState>(key: K, value: PeriodState[K]) => {
    setPeriods((prev) => ({ ...prev, [key]: value }));
  };

  const handleUniversalFilterChange = (period: PeriodOption) => {
    setPeriods((prev) => ({
      ...prev,
      dailyActiveUsers: period,
      vsGames: period,
      singleGames: period,
      dailyChallenge: period,
      completedVsGames: period,
      completedVsBots: period,
    }));
  };

  // Computed stats
  const getPeriodStats = useMemo(() => {
    return (period: PeriodOption) => {
      let data: ChartDataPoint[] = [];
      let currentIndex = -1;
      let previousIndex = -1;

      switch (period) {
        case '1D':
          data = dailyData;
          currentIndex = data.length - 1;
          previousIndex = data.length - 2;
          break;
        case '7D':
          data = dailyData;
          currentIndex = data.length - 1;
          previousIndex = 0;
          break;
        case '30D':
          data = weeklyData;
          currentIndex = data.length - 1;
          previousIndex = data.length - 2;
          break;
        case '90D':
          data = monthlyData;
          currentIndex = data.length - 1;
          previousIndex = data.length - 2;
          break;
      }

      if (data.length === 0 || currentIndex < 0) {
        return {
          current: { daily: 0, vsRandom: 0, vsFriends: 0, vsBot: 0, single: 0, vsTotal: 0, total: 0 },
          previous: { daily: 0, vsRandom: 0, vsFriends: 0, vsBot: 0, single: 0, vsTotal: 0, total: 0 },
          changes: { daily: 0, vsRandom: 0, vsFriends: 0, vsBot: 0, single: 0, vsTotal: 0, total: 0 },
          periodData: [],
        };
      }

      if (period === '7D') {
        const weekSummed = data.reduce(
          (acc, d) => ({
            daily: acc.daily + d.daily,
            vsRandom: acc.vsRandom + d.vsRandom,
            vsFriends: acc.vsFriends + d.vsFriends,
            vsBot: acc.vsBot + d.vsBot,
            single: acc.single + d.single,
            vsGamesStarted: acc.vsGamesStarted + (d.vsGamesStarted || 0),
          }),
          { daily: 0, vsRandom: 0, vsFriends: 0, vsBot: 0, single: 0, vsGamesStarted: 0 }
        );

        // Use vsGamesStarted from DailyActivityLog (or GameBoard for today)
        const weekVsTotal = weekSummed.vsGamesStarted + weekSummed.vsBot;
        const weekTotal = weekVsTotal + weekSummed.single + weekSummed.daily;
        const firstDay = data[0] || { daily: 0, vsRandom: 0, vsFriends: 0, vsBot: 0, single: 0, vsGamesStarted: 0 };
        const lastDay = data[data.length - 1] || firstDay;

        return {
          current: { ...weekSummed, vsTotal: weekVsTotal, total: weekTotal },
          previous: { ...firstDay, vsTotal: (firstDay.vsGamesStarted || 0) + firstDay.vsBot, total: 0 },
          changes: {
            daily: calculatePercentageChange(firstDay.daily, lastDay.daily),
            vsRandom: calculatePercentageChange(firstDay.vsRandom, lastDay.vsRandom),
            vsFriends: calculatePercentageChange(firstDay.vsFriends, lastDay.vsFriends),
            vsBot: calculatePercentageChange(firstDay.vsBot, lastDay.vsBot),
            single: calculatePercentageChange(firstDay.single, lastDay.single),
            vsTotal: calculatePercentageChange(
              (firstDay.vsGamesStarted || 0) + firstDay.vsBot,
              (lastDay.vsGamesStarted || 0) + lastDay.vsBot
            ),
            total: 0,
          },
          periodData: data,
        };
      }

      const currentData = data[currentIndex];
      const previousData = previousIndex >= 0 ? data[previousIndex] : { daily: 0, vsRandom: 0, vsFriends: 0, vsBot: 0, single: 0, vsGamesStarted: 0 };

      // Use vsGamesStarted from DailyActivityLog (or GameBoard for today)
      const currentVsTotal = (currentData.vsGamesStarted || 0) + currentData.vsBot;
      const previousVsTotal = (previousData.vsGamesStarted || 0) + previousData.vsBot;
      const currentTotal = currentVsTotal + currentData.single + currentData.daily;
      const previousTotal = previousVsTotal + previousData.single + previousData.daily;

      return {
        current: { ...currentData, vsTotal: currentVsTotal, total: currentTotal },
        previous: { ...previousData, vsTotal: previousVsTotal, total: previousTotal },
        changes: {
          daily: calculatePercentageChange(previousData.daily, currentData.daily),
          vsRandom: calculatePercentageChange(previousData.vsRandom, currentData.vsRandom),
          vsFriends: calculatePercentageChange(previousData.vsFriends, currentData.vsFriends),
          vsBot: calculatePercentageChange(previousData.vsBot, currentData.vsBot),
          single: calculatePercentageChange(previousData.single, currentData.single),
          vsTotal: calculatePercentageChange(previousVsTotal, currentVsTotal),
          total: calculatePercentageChange(previousTotal, currentTotal),
        },
        periodData: data,
      };
    };
  }, [dailyData, weeklyData, monthlyData]);

  // Stat calculations
  const getActiveUsersLabel = (period: PeriodOption): string => {
    switch (period) {
      case '1D': return 'Daily Active Users';
      case '7D': return 'Weekly Active Users';
      case '30D': return 'Monthly Active Users';
      case '90D': return 'Quarterly Active Users';
    }
  };

  const dailyActiveUsersStat = useMemo(() => {
    const period = periods.dailyActiveUsers;
    const label = getActiveUsersLabel(period);

    if (!statsData) return { label, value: '---', change: 'Loading...', positive: true };

    let usersValue = 0;
    let usersChange = 0;
    let hasData = true;

    if (period === '1D') {
      usersValue = statsData.dailyUsers?.current || 0;
      usersChange = statsData.dailyUsers?.percentageChange || 0;
    } else if (period === '7D') {
      usersValue = statsData.weeklyUsers?.current || 0;
      usersChange = statsData.weeklyUsers?.percentageChange || 0;
    } else if (period === '30D') {
      usersValue = statsData.monthlyUsers?.current || 0;
      usersChange = statsData.monthlyUsers?.percentageChange || 0;
    } else {
      // 90D: Use quarterlyUsers from API
      const quarterlyValue = statsData.quarterlyUsers?.current || 0;
      const quarterlyChange = statsData.quarterlyUsers?.percentageChange;

      // If no quarterly data available, show N/A
      if (quarterlyValue === 0 || quarterlyChange === undefined) {
        hasData = false;
      } else {
        usersValue = quarterlyValue;
        usersChange = quarterlyChange;
      }
    }

    if (!hasData) {
      return {
        label,
        value: 'N/A',
        change: 'Insufficient data',
        positive: true,
      };
    }

    return {
      label,
      value: usersValue.toLocaleString(),
      change: `${usersChange >= 0 ? '+' : ''}${usersChange.toFixed(2)}% ${getPeriodLabel(period)}`,
      positive: usersChange >= 0,
    };
  }, [statsData, periods.dailyActiveUsers]);

  const vsGamesStat = useMemo(() => {
    const period = periods.vsGames;

    // Use API comparison data for accurate period-over-period comparisons
    // 1D falls back to the old calculation since API doesn't have 1D comparison
    if (period === '7D' && vsGames7DComparison) {
      return {
        label: 'VS Games',
        value: vsGames7DComparison.currentPeriodTotal.toLocaleString(),
        change: `${vsGames7DComparison.percentageChange >= 0 ? '+' : ''}${vsGames7DComparison.percentageChange.toFixed(2)}% ${getPeriodLabel(period)}`,
        positive: vsGames7DComparison.percentageChange >= 0,
      };
    }
    if (period === '30D' && vsGames30DComparison) {
      return {
        label: 'VS Games',
        value: vsGames30DComparison.currentPeriodTotal.toLocaleString(),
        change: `${vsGames30DComparison.percentageChange >= 0 ? '+' : ''}${vsGames30DComparison.percentageChange.toFixed(2)}% ${getPeriodLabel(period)}`,
        positive: vsGames30DComparison.percentageChange >= 0,
      };
    }
    if (period === '90D' && vsGames90DComparison) {
      return {
        label: 'VS Games',
        value: vsGames90DComparison.currentPeriodTotal.toLocaleString(),
        change: `${vsGames90DComparison.percentageChange >= 0 ? '+' : ''}${vsGames90DComparison.percentageChange.toFixed(2)}% ${getPeriodLabel(period)}`,
        positive: vsGames90DComparison.percentageChange >= 0,
      };
    }

    // Fallback for 1D - use today's data from DailyActivityLog/GameBoard
    const periodStats = getPeriodStats(period);
    // For 1D, use current day value; for other periods, sum all days using vsGamesStarted
    let vsTotal = 0;
    if (period === '1D') {
      vsTotal = periodStats.current.vsTotal;
    } else {
      for (const d of periodStats.periodData) {
        vsTotal += (d.vsGamesStarted || 0) + d.vsBot;
      }
    }
    return {
      label: 'VS Games',
      value: vsTotal.toLocaleString(),
      change: `${periodStats.changes.vsTotal >= 0 ? '+' : ''}${periodStats.changes.vsTotal.toFixed(2)}% ${getPeriodLabel(period)}`,
      positive: periodStats.changes.vsTotal >= 0,
    };
  }, [periods.vsGames, getPeriodStats, vsGames7DComparison, vsGames30DComparison, vsGames90DComparison]);

  const singleGamesStat = useMemo(() => {
    const period = periods.singleGames;

    // Use API comparison data for accurate period-over-period comparisons
    if (period === '7D' && singleGames7DComparison) {
      return {
        label: 'Single Games',
        value: singleGames7DComparison.currentPeriodTotal.toLocaleString(),
        change: `${singleGames7DComparison.percentageChange >= 0 ? '+' : ''}${singleGames7DComparison.percentageChange.toFixed(2)}% ${getPeriodLabel(period)}`,
        positive: singleGames7DComparison.percentageChange >= 0,
      };
    }
    if (period === '30D' && singleGames30DComparison) {
      return {
        label: 'Single Games',
        value: singleGames30DComparison.currentPeriodTotal.toLocaleString(),
        change: `${singleGames30DComparison.percentageChange >= 0 ? '+' : ''}${singleGames30DComparison.percentageChange.toFixed(2)}% ${getPeriodLabel(period)}`,
        positive: singleGames30DComparison.percentageChange >= 0,
      };
    }
    if (period === '90D' && singleGames90DComparison) {
      return {
        label: 'Single Games',
        value: singleGames90DComparison.currentPeriodTotal.toLocaleString(),
        change: `${singleGames90DComparison.percentageChange >= 0 ? '+' : ''}${singleGames90DComparison.percentageChange.toFixed(2)}% ${getPeriodLabel(period)}`,
        positive: singleGames90DComparison.percentageChange >= 0,
      };
    }

    // Fallback for 1D - use today's data from DailyActivityLog/GameBoard
    const periodStats = getPeriodStats(period);
    // For 1D, use current day value; for other periods, sum all days
    let singleTotal = 0;
    if (period === '1D') {
      singleTotal = periodStats.current.single;
    } else {
      for (const d of periodStats.periodData) {
        singleTotal += d.single;
      }
    }
    return {
      label: 'Single Games',
      value: singleTotal.toLocaleString(),
      change: `${periodStats.changes.single >= 0 ? '+' : ''}${periodStats.changes.single.toFixed(2)}% ${getPeriodLabel(period)}`,
      positive: periodStats.changes.single >= 0,
    };
  }, [periods.singleGames, getPeriodStats, singleGames7DComparison, singleGames30DComparison, singleGames90DComparison]);

  const dailyChallengeStatsData = useMemo(() => {
    if (!statsData?.dailyChallenge) {
      return [
        { label: 'Completed:', value: '---', change: '---', positive: true },
        { label: 'Failed:', value: '---', change: '---', positive: false },
        { label: 'Success Rate:', value: '---', change: '---', positive: true },
      ];
    }

    const period = periods.dailyChallenge;
    const periodStats = getPeriodStats(period);
    let total = 0;
    let completed = 0;
    let failed = 0;

    // Get the API comparison data for proper period-over-period comparison
    const comparisonData = period === '7D' ? dailyChallenge7DComparison
      : period === '30D' ? dailyChallenge30DComparison
      : period === '90D' ? dailyChallenge90DComparison
      : undefined;

    if (period === '1D') {
      const challenge = statsData.dailyChallenge;
      completed = challenge.completed || 0;
      failed = challenge.failed || 0;
      total = completed + failed;
    } else if (comparisonData) {
      // Use API comparison data for accurate totals (from DailyActivityLog)
      total = comparisonData.currentPeriodTotal;
      // For completed/failed breakdown, sum from period data
      for (const d of periodStats.periodData) {
        completed += d.dailyCompleted || 0;
        failed += d.dailyFailed || 0;
      }
    } else {
      // Fallback: sum from period data (from DailyActivityLog)
      for (const d of periodStats.periodData) {
        total += d.daily;
        completed += d.dailyCompleted || 0;
        failed += d.dailyFailed || 0;
      }
    }

    // Use API comparison for percentage change, fallback to local calculation for 1D
    const dailyChange = comparisonData?.percentageChange ?? periodStats.changes.daily;

    const successRate = total > 0 ? (completed / total) * 100 : 0;
    const failedPercent = total > 0 ? (failed / total * 100) : 0;
    const completedIsNoChange = dailyChange === 0;

    return [
      { label: 'Completed:', value: completed.toString(), change: `${dailyChange >= 0 ? '+' : ''}${dailyChange.toFixed(2)}%`, positive: dailyChange >= 0, neutral: completedIsNoChange },
      { label: 'Failed:', value: failed.toString(), change: `${failedPercent.toFixed(1)}%`, positive: false, neutral: true },
      { label: 'Success Rate:', value: `${successRate.toFixed(1)}%`, change: successRate >= 50 ? 'Good' : 'Needs improvement', positive: successRate >= 50, neutral: true },
    ];
  }, [statsData, periods.dailyChallenge, getPeriodStats, dailyChallenge7DComparison, dailyChallenge30DComparison, dailyChallenge90DComparison]);

  const dailyChallengeMainStat = useMemo(() => {
    if (!statsData?.dailyChallenge) {
      return { value: '---', change: 'Loading...', changePositive: true, successRate: '---' };
    }

    const period = periods.dailyChallenge;
    const periodStats = getPeriodStats(period);

    // Get the API comparison data for proper period-over-period comparison
    const comparisonData = period === '7D' ? dailyChallenge7DComparison
      : period === '30D' ? dailyChallenge30DComparison
      : period === '90D' ? dailyChallenge90DComparison
      : undefined;

    let total = 0;
    let completed = 0;

    if (period === '1D') {
      // For 1D, use live data from statsData (queried from GameBoard today)
      const challenge = statsData.dailyChallenge;
      completed = challenge.completed || 0;
      total = completed + (challenge.failed || 0);
    } else if (comparisonData) {
      // Use API comparison data for accurate totals (from DailyActivityLog)
      total = comparisonData.currentPeriodTotal;
      for (const d of periodStats.periodData) {
        completed += d.dailyCompleted || 0;
      }
    } else {
      // Fallback: sum from period data (from DailyActivityLog)
      for (const d of periodStats.periodData) {
        total += d.daily;
        completed += d.dailyCompleted || 0;
      }
    }

    // Use API comparison for percentage change, fallback to local calculation for 1D
    const dailyChange = comparisonData?.percentageChange ?? periodStats.changes.daily;
    const successRate = total > 0 ? (completed / total) * 100 : 0;

    return {
      value: total.toString(),
      change: `${dailyChange >= 0 ? '+' : ''}${dailyChange.toFixed(2)}% ${getPeriodLabel(period)}`,
      changePositive: dailyChange >= 0,
      successRate: `${successRate.toFixed(0)}%`,
    };
  }, [statsData, periods.dailyChallenge, getPeriodStats, dailyChallenge7DComparison, dailyChallenge30DComparison, dailyChallenge90DComparison]);

  // Bottom stats
  const completedVsGamesStat = useMemo(() => {
    const period = periods.completedVsGames;
    const periodStats = getPeriodStats(period);

    // Use API comparison data for accurate period-over-period comparisons (from DailyActivityLog)
    // Use vsGamesCompleted from DailyActivityLog for bars
    const getBars = () => periodStats.periodData.map((d) => d.vsGamesCompleted || 0);

    if (period === '7D' && vsGamesCompleted7DComparison) {
      return {
        label: 'Completed VS Games',
        value: vsGamesCompleted7DComparison.currentPeriodTotal.toLocaleString(),
        change: `${vsGamesCompleted7DComparison.percentageChange >= 0 ? '+' : ''}${vsGamesCompleted7DComparison.percentageChange.toFixed(2)}% ${getPeriodLabel(period)}`,
        positive: vsGamesCompleted7DComparison.percentageChange >= 0,
        bars: getBars(),
      };
    }
    if (period === '30D' && vsGamesCompleted30DComparison) {
      return {
        label: 'Completed VS Games',
        value: vsGamesCompleted30DComparison.currentPeriodTotal.toLocaleString(),
        change: `${vsGamesCompleted30DComparison.percentageChange >= 0 ? '+' : ''}${vsGamesCompleted30DComparison.percentageChange.toFixed(2)}% ${getPeriodLabel(period)}`,
        positive: vsGamesCompleted30DComparison.percentageChange >= 0,
        bars: getBars(),
      };
    }
    if (period === '90D' && vsGamesCompleted90DComparison) {
      return {
        label: 'Completed VS Games',
        value: vsGamesCompleted90DComparison.currentPeriodTotal.toLocaleString(),
        change: `${vsGamesCompleted90DComparison.percentageChange >= 0 ? '+' : ''}${vsGamesCompleted90DComparison.percentageChange.toFixed(2)}% ${getPeriodLabel(period)}`,
        positive: vsGamesCompleted90DComparison.percentageChange >= 0,
        bars: getBars(),
      };
    }

    // Fallback for 1D - use today's data from GameBoard, compare to yesterday from DailyActivityLog
    const currentCompleted = periodStats.periodData[periodStats.periodData.length - 1]?.vsGamesCompleted || 0;
    const previousCompleted = periodStats.periodData[periodStats.periodData.length - 2]?.vsGamesCompleted || 0;
    const completedChange = calculatePercentageChange(previousCompleted, currentCompleted);

    return {
      label: 'Completed VS Games',
      value: currentCompleted.toLocaleString(),
      change: `${completedChange >= 0 ? '+' : ''}${completedChange.toFixed(2)}% ${getPeriodLabel(period)}`,
      positive: completedChange >= 0,
      bars: getBars(),
    };
  }, [periods.completedVsGames, getPeriodStats, vsGamesCompleted7DComparison, vsGamesCompleted30DComparison, vsGamesCompleted90DComparison]);

  const vsBotsStat = useMemo(() => {
    const period = periods.completedVsBots;
    const periodStats = getPeriodStats(period);

    // Use vsBot field from DailyActivityLog (BotPracticeGamesPlayed) for bars
    const getBars = () => periodStats.periodData.map((d) => d.vsBot || 0);

    // Use API comparison data for accurate period-over-period comparisons
    if (period === '7D' && vsBot7DComparison) {
      return {
        label: 'VS Bots',
        value: vsBot7DComparison.currentPeriodTotal.toLocaleString(),
        change: `${vsBot7DComparison.percentageChange >= 0 ? '+' : ''}${vsBot7DComparison.percentageChange.toFixed(2)}% ${getPeriodLabel(period)}`,
        positive: vsBot7DComparison.percentageChange >= 0,
        bars: getBars(),
      };
    }
    if (period === '30D' && vsBot30DComparison) {
      return {
        label: 'VS Bots',
        value: vsBot30DComparison.currentPeriodTotal.toLocaleString(),
        change: `${vsBot30DComparison.percentageChange >= 0 ? '+' : ''}${vsBot30DComparison.percentageChange.toFixed(2)}% ${getPeriodLabel(period)}`,
        positive: vsBot30DComparison.percentageChange >= 0,
        bars: getBars(),
      };
    }
    if (period === '90D' && vsBot90DComparison) {
      return {
        label: 'VS Bots',
        value: vsBot90DComparison.currentPeriodTotal.toLocaleString(),
        change: `${vsBot90DComparison.percentageChange >= 0 ? '+' : ''}${vsBot90DComparison.percentageChange.toFixed(2)}% ${getPeriodLabel(period)}`,
        positive: vsBot90DComparison.percentageChange >= 0,
        bars: getBars(),
      };
    }

    // Fallback for 1D - use today's data from data points
    const currentVsBots = periodStats.periodData[periodStats.periodData.length - 1]?.vsBot || 0;
    const previousVsBots = periodStats.periodData[periodStats.periodData.length - 2]?.vsBot || 0;
    const change = calculatePercentageChange(previousVsBots, currentVsBots);

    return {
      label: 'VS Bots',
      value: currentVsBots.toLocaleString(),
      change: `${change >= 0 ? '+' : ''}${change.toFixed(2)}% ${getPeriodLabel(period)}`,
      positive: change >= 0,
      bars: getBars(),
    };
  }, [periods.completedVsBots, getPeriodStats, vsBot7DComparison, vsBot30DComparison, vsBot90DComparison]);

  // Styles
  const styles = {
    container: {
      padding: isMobile ? '12px' : '20px',
      maxWidth: '1600px',
      margin: '0 auto',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      flexWrap: 'wrap' as const,
      gap: '12px',
    },
    title: {
      fontSize: isMobile ? '24px' : '28px',
      fontWeight: 700,
      color: theme.text,
      margin: 0,
    },
    topStatsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : '1fr 1fr 1fr 1.4fr',
      gap: '12px',
      marginBottom: '16px',
    },
    middleSection: {
      display: 'grid',
      gridTemplateColumns: isMobile || isTablet ? '1fr' : '1fr 320px',
      gap: '12px',
      marginBottom: '16px',
    },
    rightSidebar: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px',
    },
    bottomStatsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)',
      gap: '12px',
    },
    loadingCard: {
      background: theme.card,
      borderRadius: '8px',
      padding: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '120px',
    },
    errorCard: {
      background: theme.card,
      borderRadius: '8px',
      padding: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      marginBottom: '16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    spinner: {
      border: '3px solid rgba(255,255,255,0.3)',
      borderTop: '3px solid #2563eb',
      borderRadius: '50%',
      width: '30px',
      height: '30px',
      animation: 'spin 1s linear infinite',
    },
  };

  return (
    <div style={styles.container}>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Admin Dashboard</h1>
        <UniversalFilter onChangeAll={handleUniversalFilterChange} theme={theme} />
      </div>

      {/* Error Banner */}
      {statsError && (
        <div style={styles.errorCard}>
          <div>
            <div style={{ color: '#ef4444', fontWeight: 600, marginBottom: '4px' }}>Failed to load statistics</div>
            <div style={{ color: theme.textSecondary, fontSize: '14px' }}>{statsError}</div>
          </div>
          <button
            onClick={retry}
            style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Top Stats */}
      <div style={styles.topStatsGrid}>
        {statsLoading ? (
          [1, 2, 3, 4].map((i) => (
            <div key={i} style={styles.loadingCard}>
              <div style={styles.spinner} />
            </div>
          ))
        ) : (
          <>
            <StatCard {...dailyActiveUsersStat} theme={theme} isMobile={isMobile} period={periods.dailyActiveUsers} onPeriodChange={(p) => updatePeriod('dailyActiveUsers', p as PeriodOption)} />
            <StatCard {...vsGamesStat} theme={theme} isMobile={isMobile} period={periods.vsGames} onPeriodChange={(p) => updatePeriod('vsGames', p as PeriodOption)} />
            <StatCard {...singleGamesStat} theme={theme} isMobile={isMobile} period={periods.singleGames} onPeriodChange={(p) => updatePeriod('singleGames', p as PeriodOption)} />
            <DailyChallengeCard
              periodTitle="Daily"
              mainValue={dailyChallengeMainStat.value}
              mainChange={dailyChallengeMainStat.change}
              mainChangePositive={dailyChallengeMainStat.changePositive}
              successRate={dailyChallengeMainStat.successRate}
              stats={dailyChallengeStatsData}
              theme={theme}
              isMobile={isMobile}
              period={periods.dailyChallenge}
              onPeriodChange={(p) => updatePeriod('dailyChallenge', p as PeriodOption)}
            />
          </>
        )}
      </div>

      {/* Chart + Right SideArea */}
      <div style={styles.middleSection}>
        <ChartCard
          chartData={chartData}
          chartPeriod={periods.chart}
          onPeriodChange={(p) => updatePeriod('chart', p)}
          totalGamesPlayed={totalGamesPlayed}
          activeLines={activeLines}
          onToggleLine={(key) => setActiveLines((prev) => ({ ...prev, [key]: !prev[key] }))}
          loading={chartLoading}
          theme={theme}
          isMobile={isMobile}
        />

        <div style={styles.rightSidebar}>
          <RevenueCard value="$3,403.79" change="+7.85% from last month" period="30D" isMobile={isMobile} />
          <PerformersCard performers={topPerformers} loading={topPerformersLoading} period={periods.topPerformers} onPeriodChange={(p) => updatePeriod('topPerformers', p as PeriodOption)} onViewPerformer={(performer) => performer.userId && navigate(`/Admin/Users/Players/${performer.userId}`)} theme={theme} />
        </div>
      </div>

      {/* Bottom Stats */}
      <div style={styles.bottomStatsGrid}>
        <BottomStatCard {...completedVsGamesStat} theme={theme} period={periods.completedVsGames} onPeriodChange={(p) => updatePeriod('completedVsGames', p as PeriodOption)} />
        <BottomStatCard {...vsBotsStat} theme={theme} period={periods.completedVsBots} onPeriodChange={(p) => updatePeriod('completedVsBots', p as PeriodOption)} />
        <BottomStatCard label="Board Reward Ads" value="---" change="Coming soon" positive bars={[]} theme={theme} periodBadge="N/A" barColor="#6b7280" disabled />
        <BottomStatCard label="Hint Reward Ads" value="---" change="Coming soon" positive bars={[]} theme={theme} periodBadge="N/A" barColor="#6b7280" disabled />
        <BottomStatCard label="Interstitial Video Ads" value="---" change="Coming soon" positive bars={[]} theme={theme} periodBadge="N/A" barColor="#6b7280" disabled />
      </div>
    </div>
  );
};

export default Dashboard;
